import { useTranslation } from 'react-i18next';

import { BodyListSeed } from '@/components/detail/list-seed';
import { ListingLinkPills } from '@/components/detail/listing-link-pills';
import { RelatedList } from '@/components/detail/related-list';
import { BoatCard } from '@/components/riviera/boat-card';
import { TourCard } from '@/components/riviera/tour-card';
import { useFooterCityLinks } from '@/hooks/use-city-listing-pages';
import { useCityCrossSell } from '@/hooks/use-related';
import { useLocale } from '@todo/core/i18n/use-locale';
import { crossSellLinks } from '@/lib/listing-links';

/**
 * "Tours & boat rental in Nice": the city's top tours and boats as cards, then
 * links to its tours, boat-rental and wine-tour pages (or the hubs, where the
 * city has none exported). Placed on the event and article pages, which get
 * almost all of the site's search clicks while the affiliate catalogues still
 * rank far down — so the traffic that lands reaches something bookable, and
 * the landing pages get links from the pages Google already likes.
 *
 * Self-contained: it bakes its own two small seeds into the body
 * (BodyListSeed), so a page drops it in without threading keys into its head.
 * On web, place it inside the page's data-lastmod-skip region — the cards
 * change with the catalogue, not with the page.
 */
export function CityCrossSell({
  cityId,
  omitLinkKeys = [],
}: {
  cityId: string | null | undefined;
  /** Links the page already shows elsewhere (an article's "Plan your trip"). */
  omitLinkKeys?: string[];
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const pages = useFooterCityLinks();
  const { items, cityId: knownCity, toursSeedKey, boatsSeedKey } = useCityCrossSell(cityId);

  if (items.length === 0) return null;

  const links = crossSellLinks(knownCity, pages, locale, t).filter(
    (link) => !omitLinkKeys.includes(link.key),
  );
  const heading = knownCity
    ? t('crossSell.headingCity', { inCity: t(`boatRental.cities.${knownCity}.inCity`) })
    : t('crossSell.heading');

  return (
    <>
      <BodyListSeed seedKey={toursSeedKey} />
      <BodyListSeed seedKey={boatsSeedKey} />
      <RelatedList
        heading={heading}
        footer={links.length > 0 ? <ListingLinkPills links={links} /> : null}>
        {items.map((item) =>
          item.kind === 'tour' ? (
            <TourCard key={`tour:${item.tour.id}`} tour={item.tour} />
          ) : (
            <BoatCard key={`boat:${item.boat.id}`} boat={item.boat} />
          ),
        )}
      </RelatedList>
    </>
  );
}
